import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { ReactNode, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'next-i18next'
import { clsx } from 'clsx'

interface SelectProps<TValue extends string | null | undefined> {
  value: TValue
  onChange: (x: TValue) => void
  children: ReactNode
  className?: string
  dropdownPanelClassName?: string
  placeholder?: string
  disabled?: boolean
  renderValue?: (value: TValue) => string | ReactNode
}

function Select<TValue extends string | null | undefined>({
  value,
  onChange,
  children,
  className = '',
  dropdownPanelClassName,
  placeholder,
  disabled = false,
  renderValue = (v) => v,
}: SelectProps<TValue>) {
  const { t } = useTranslation('components')
  const buttonRef = useRef<HTMLButtonElement>(null)

  return (
    <div className={clsx('relative', className)}>
      <Listbox value={value} onChange={onChange} disabled={disabled}>
        {({ open }) => (
          <>
            <ListboxButton
              ref={buttonRef}
              className="default-transition size-full rounded-md bg-th-input-bkg py-2.5 font-normal ring-1 ring-inset ring-th-input-border focus:border-th-input-border-hover focus:outline-none md:hover:ring-th-input-border-hover"
            >
              <div className="flex items-center justify-between space-x-2 px-3 text-th-fgd-1">
                {value ? (
                  renderValue(value)
                ) : (
                  <span className="text-th-fgd-3">
                    {placeholder ?? t('forms.select.placeholder')}
                  </span>
                )}
                <ChevronDownIcon
                  className={clsx(
                    'default-transition size-5 shrink-0 text-th-fgd-1',
                    open ? 'rotate-180' : 'rotate-0',
                  )}
                />
              </div>
            </ListboxButton>
            {open && buttonRef.current
              ? createPortal(
                  <div
                    className="z-50"
                    style={{
                      position: 'fixed',
                      top: buttonRef.current.getBoundingClientRect().bottom,
                      left: buttonRef.current.getBoundingClientRect().left,
                      width: buttonRef.current.offsetWidth,
                    }}
                  >
                    <ListboxOptions
                      static
                      className={clsx(
                        'thin-scroll absolute z-50 mt-1 max-h-60 w-full origin-top-left space-y-2 overflow-auto rounded-md bg-th-bkg-2 p-4 text-th-fgd-1 outline-none',
                        dropdownPanelClassName,
                      )}
                    >
                      {children}
                    </ListboxOptions>
                  </div>,
                  document.body,
                )
              : null}
          </>
        )}
      </Listbox>
    </div>
  )
}

interface OptionProps {
  value: string
  children: string | ReactNode
  className?: string
}

const Option = ({ value, children, className = '' }: OptionProps) => {
  return (
    <ListboxOption className="mb-0" value={value}>
      {({ selected }) => (
        <div
          className={clsx(
            'default-transition rounded text-th-fgd-2 hover:cursor-pointer md:hover:text-th-fgd-1',
            selected && 'text-th-active',
            className,
          )}
        >
          {children}
        </div>
      )}
    </ListboxOption>
  )
}

Select.Option = Option

export default Select
